import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/** Plans sold through the Lemon Squeezy Checkouts API (Spec: fix/ls-checkout-bound-to-variant). */
export const checkoutTierSchema = z.enum(["PRO", "ULTIMATE"]);
export type CheckoutTier = z.infer<typeof checkoutTierSchema>;

export const checkoutIntervalSchema = z.enum(["weekly", "monthly", "yearly"]);
export type CheckoutInterval = z.infer<typeof checkoutIntervalSchema>;
/** Correlation only; never authorizes a payment or entitlement. */
export const checkoutAttemptIdSchema = z.string().uuid();

/**
 * Body of `POST /billing/checkouts`.
 *
 * `mode` defaults to a full hosted page (the embedded overlay 403s when third-party
 * cookies are blocked); "overlay" is kept for the legacy embedded flow.
 * The variant id, buyer email, and
 * user id are all resolved/attached server-side (`BillingService.createCheckout`)
 * so the client never has to know a Lemon Squeezy variant id exists.
 */
export const createCheckoutSchema = z.object({
  tier: checkoutTierSchema,
  interval: checkoutIntervalSchema,
  mode: z.enum(["overlay", "hosted"]).optional(),
  attemptId: checkoutAttemptIdSchema.optional(),
  surface: z
    .string()
    .regex(/^[a-z][\d_a-z]{0,63}$/)
    .optional(),
});

export class CreateCheckoutDto extends createZodDto(createCheckoutSchema) {}

export const checkoutReturnSchema = z.object({
  attemptId: checkoutAttemptIdSchema.optional(),
  marker: z.enum(["success", "cancelled", "bare"]),
});
export class CheckoutReturnDto extends createZodDto(checkoutReturnSchema) {}

/** Response of `POST /billing/checkouts`. */
export const checkoutResponseSchema = z.object({
  url: z.string().url(),
  /** ISO timestamp the checkout session expires, when created via the API. */
  expiresAt: z.string().optional(),
  /** The Lemon Squeezy variant id the checkout was bound to, when known. */
  variantId: z.string().optional(),
  /**
   * True when the LS Checkouts API was unavailable/failed and `url` is the
   * static, product-level fallback checkout URL instead of an API-created,
   * variant-bound one. Present and explicit (`false` on success, `true` on
   * fallback) rather than merely absent, so the client never has to guess.
   *
   * The static fallback pre-selects Monthly on Lemon Squeezy's hosted page
   * (that is the bug this endpoint exists to fix), so it is only ever safe
   * for `interval: "monthly"`. A weekly/yearly failure never sets this —
   * `createCheckout` throws `CHECKOUT_UNAVAILABLE` instead (see below).
   */
  fallback: z.boolean(),
});
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

/**
 * Stable error code on the `POST /billing/checkouts` failure body when no
 * safe checkout URL can be returned — i.e. every failure mode (missing API
 * key, LS API error, timeout, malformed response) for a `weekly`/`yearly`
 * plan, where falling back to the static product URL would silently
 * recreate the $19.99-vs-$7.99 defect this endpoint exists to fix. The
 * client matches on this code to show a "couldn't start checkout" toast
 * rather than a generic error.
 */
export const CHECKOUT_UNAVAILABLE_CODE = "CHECKOUT_UNAVAILABLE";
